import { apiClient } from './client';
import type { LoginResponse } from './auth';

export interface TwoFactorSetupResponse {
  secret: string;
  qrCodeUrl: string;
}

export interface TwoFactorSetupVerifyResponse {
  success: boolean;
  recoveryCodes: string[];
}

export interface TwoFactorVerifyResponse extends LoginResponse {
  remainingRecoveryCodes?: number;
}

export interface TwoFactorStatusResponse {
  enabled: boolean;
  required: boolean;
  recoveryCodesRemaining: number;
}

export interface RecoveryCodesStatusResponse {
  unusedCount: number;
  totalCount: number;
}

export interface TwoFactorChallengeResponse {
  requiresTwoFactor: true;
  challengeToken: string;
  expiresAt: string;
}

export interface TwoFactorSetupRequiredResponse extends LoginResponse {
  requiresTwoFactorSetup: true;
}

export async function setup2FA(): Promise<TwoFactorSetupResponse> {
  return apiClient.post<TwoFactorSetupResponse>('/auth/2fa/setup');
}

export async function verify2FASetup(
  token: string,
  secret: string
): Promise<TwoFactorSetupVerifyResponse> {
  return apiClient.post<TwoFactorSetupVerifyResponse>('/auth/2fa/verify-setup', {
    token,
    secret,
  });
}

export async function verify2FA(
  challengeToken: string,
  token: string
): Promise<TwoFactorVerifyResponse> {
  const response = await apiClient.post<TwoFactorVerifyResponse>('/auth/2fa/verify', {
    challengeToken,
    token,
  });

  // Set CSRF token for subsequent requests (auth tokens are in HTTP-only cookies)
  apiClient.setCsrfToken(response.csrfToken);

  return response;
}

export async function useRecoveryCode(
  challengeToken: string,
  recoveryCode: string
): Promise<TwoFactorVerifyResponse> {
  const response = await apiClient.post<TwoFactorVerifyResponse>('/auth/2fa/recovery', {
    challengeToken,
    recoveryCode,
  });

  // Set CSRF token for subsequent requests (auth tokens are in HTTP-only cookies)
  apiClient.setCsrfToken(response.csrfToken);

  return response;
}

export async function disable2FA(password: string, token: string): Promise<{ success: boolean }> {
  return apiClient.post<{ success: boolean }>('/auth/2fa/disable', {
    password,
    token,
  });
}

export async function get2FAStatus(): Promise<TwoFactorStatusResponse> {
  return apiClient.get<TwoFactorStatusResponse>('/auth/2fa/status');
}

export async function getRecoveryCodesStatus(): Promise<RecoveryCodesStatusResponse> {
  return apiClient.get<RecoveryCodesStatusResponse>('/auth/2fa/recovery-codes');
}

export async function regenerateRecoveryCodes(
  token: string
): Promise<TwoFactorSetupVerifyResponse> {
  return apiClient.post<TwoFactorSetupVerifyResponse>('/auth/2fa/recovery-codes/regenerate', {
    token,
  });
}
